import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"
import { insertCatalogEntry } from "../catalog_insert"

// Drives the offer line-item tree: group collapse/expand with sessionStorage
// persistence, per-parent drag & drop reordering, and the client-side
// search/filter/sort control bar. Filter and view-only sort state also live in
// sessionStorage so they survive the full #billing_offer_tree turbo-stream replace
// that every mutation performs. While a filter or non-standard sort is active,
// drag & drop is disabled (the reorder PATCH reads ALL container children).
// Forked from boq_tree_controller.js.
export default class extends Controller {
  static targets = [
    "children",
    "subtree",
    "quickAdd",
    "searchInput",
    "typeSelect",
    "sortSelect",
    "sortDirButton",
    "resetLink",
    "dragHint",
  ]

  static values = {
    documentId: Number,
    reorderUrl: String,
    catalogInsertUrl: String,
    catalogInsertError: String,
    editable: Boolean,
  }

  connect() {
    this.collapsed = new Set(JSON.parse(sessionStorage.getItem(this.storageKey) || "[]"))
    this.filterState = this.loadFilterState()
    this.captureOriginalOrder()
    this.restoreControlValues()
    this.applyVisibility()
    if (this.editableValue) this.initSortables()
    this.syncDragState()
    this.reopenQuickAddForm()
  }

  // Continuous keyboard entry: after a quick-add submit the tree re-renders;
  // reopen the same form (focused) so the user can keep typing.
  reopenQuickAddForm() {
    const reopen = window.__billingOfferReopen
    window.__billingOfferReopen = null
    if (!reopen || Date.now() - reopen.at > 5000) return

    const link = this.element.querySelector(`a[href="${reopen.href}"]`)
    if (link) link.click()
  }

  // --- Collapse / expand ---

  toggle(event) {
    const groupId = String(event.params.id)
    if (this.collapsed.has(groupId)) {
      this.collapsed.delete(groupId)
    } else {
      this.collapsed.add(groupId)
    }
    this.persistCollapsedState()
    this.applyVisibility()
  }

  expandAll() {
    this.collapsed.clear()
    this.persistCollapsedState()
    this.applyVisibility()
  }

  collapseAll() {
    this.subtreeTargets.forEach((subtree) => this.collapsed.add(String(subtree.dataset.groupId)))
    this.persistCollapsedState()
    this.applyVisibility()
  }

  persistCollapsedState() {
    sessionStorage.setItem(this.storageKey, JSON.stringify([...this.collapsed]))
  }

  // --- Filter / sort state ---

  defaultFilterState() {
    return { query: "", type: "", sortKey: "standard", sortDir: "asc" }
  }

  loadFilterState() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.filterStorageKey) || "{}")
      return { ...this.defaultFilterState(), ...stored }
    } catch {
      return this.defaultFilterState()
    }
  }

  persistFilterState() {
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(this.filterState))
  }

  restoreControlValues() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = this.filterState.query
    if (this.hasTypeSelectTarget) this.restoreSelect(this.typeSelectTarget, this.filterState.type)
    if (this.hasSortSelectTarget)
      this.restoreSelect(this.sortSelectTarget, this.filterState.sortKey)
    this.updateSortDirIcon()
  }

  // Select the real option for `value`, skipping SlimSelect's synthetic placeholder
  // option (empty text, empty value) that shares value "" with the "all" option and
  // would otherwise win a plain `.value =` assignment. SlimSelect also ignores
  // programmatic selectedness changes, so rebuild its data afterwards; on the initial
  // connect the select controller doesn't exist yet — that's fine, SlimSelect reads
  // the already-set selectedness when it initializes right after.
  restoreSelect(selectEl, value) {
    const options = [...selectEl.options]
    const target = options.find((o) => o.value === value && o.textContent.trim() !== "")
    options.forEach((o) => (o.selected = o === target))
    this.application.getControllerForElementAndIdentifier(selectEl, "select")?.refresh()
  }

  filterChanged() {
    clearTimeout(this.filterDebounce)
    this.filterDebounce = setTimeout(() => this.commitFilterChange(), 150)
  }

  commitFilterChange() {
    this.filterState = {
      query: (this.hasSearchInputTarget ? this.searchInputTarget.value : "").trim().toLowerCase(),
      type: this.hasTypeSelectTarget ? this.typeSelectTarget.value : "",
      sortKey: this.hasSortSelectTarget ? this.sortSelectTarget.value : "standard",
      sortDir: this.filterState.sortDir,
    }
    this.persistFilterState()
    this.applyVisibility()
    this.syncDragState()
  }

  toggleSortDir() {
    this.filterState.sortDir = this.filterState.sortDir === "asc" ? "desc" : "asc"
    this.persistFilterState()
    this.updateSortDirIcon()
    this.applyVisibility()
  }

  updateSortDirIcon() {
    if (!this.hasSortDirButtonTarget) return
    const icon = this.sortDirButtonTarget.querySelector("[data-role='sort-dir-icon']")
    if (icon) icon.classList.toggle("rotate-180", this.filterState.sortDir === "desc")
  }

  resetFilters() {
    this.filterState = this.defaultFilterState()
    this.persistFilterState()
    this.restoreControlValues()
    this.applyVisibility()
    this.syncDragState()
  }

  filterActive() {
    const state = this.filterState
    return state.query !== "" || state.type !== ""
  }

  sortActive() {
    return this.filterState.sortKey !== "standard"
  }

  // --- Visibility pipeline: sort, then collapse, then filter on top ---

  applyVisibility() {
    this.applySort()
    this.applyCollapsedState()
    this.applyFilter()
  }

  applyCollapsedState() {
    this.subtreeTargets.forEach((subtree) => {
      const isCollapsed = this.collapsed.has(String(subtree.dataset.groupId))
      subtree.classList.toggle("hidden", isCollapsed)
      const chevron = this.element.querySelector(
        `[data-billing-offer-tree-chevron="${subtree.dataset.groupId}"]`,
      )
      if (chevron) chevron.classList.toggle("-rotate-90", isCollapsed)
    })
  }

  applyFilter() {
    const active = this.filterActive()
    this.quickAddTargets.forEach((el) => el.classList.toggle("hidden", active))
    if (this.hasResetLinkTarget) {
      this.resetLinkTarget.classList.toggle("hidden", !(active || this.sortActive()))
    }

    if (!active) {
      this.element
        .querySelectorAll("div[data-kind]")
        .forEach((node) => node.classList.remove("hidden"))
      return
    }

    this.rootNodes().forEach((node) => this.processNode(node, false))
  }

  // Marks a node (and subtree) visible when it matches, contains a match, or sits
  // under a matching group. Returns the number of visible positions.
  processNode(node, ancestorMatched) {
    const self = this.matchesSelf(node)
    const subtree = node.querySelector(":scope > [data-billing-offer-tree-target='subtree']")
    let descendantCount = 0
    let descendantMatched = false

    if (subtree) {
      this.childNodesOf(subtree).forEach((child) => {
        const count = this.processNode(child, ancestorMatched || self)
        descendantCount += count
        if (count > 0 || !child.classList.contains("hidden")) descendantMatched = true
      })
    }

    const visible = self || descendantMatched || ancestorMatched
    node.classList.toggle("hidden", !visible)
    if (subtree && visible) subtree.classList.remove("hidden")

    let count = descendantCount
    if (visible && node.dataset.kind === "position") count += 1
    return count
  }

  matchesSelf(node) {
    const data = node.dataset
    const state = this.filterState
    if (state.query && !(data.search || "").includes(state.query)) return false
    if (state.type && data.positionType !== state.type) return false
    return true
  }

  rootNodes() {
    const root = this.childrenTargets.find((container) => !container.dataset.parentId)
    return root ? this.nodeChildren(root) : []
  }

  childNodesOf(subtree) {
    const container = subtree.querySelector(":scope > .billing-offer-children")
    return container ? this.nodeChildren(container) : []
  }

  nodeChildren(container) {
    return Array.from(container.children).filter((el) => el.dataset && el.dataset.kind)
  }

  // --- View-only sorting (never persisted) ---

  captureOriginalOrder() {
    this.originalOrder = new Map()
    this.childrenTargets.forEach((container) => {
      this.originalOrder.set(container, this.nodeChildren(container))
    })
  }

  applySort() {
    if (!this.sortActive()) {
      this.restoreOriginalOrder()
      return
    }

    const direction = this.filterState.sortDir === "desc" ? -1 : 1
    this.childrenTargets.forEach((container) => {
      this.nodeChildren(container)
        .map((node, index) => ({ node, index }))
        .sort((a, b) => this.compareNodes(a, b, this.filterState.sortKey, direction))
        .forEach((entry) => container.appendChild(entry.node))
    })
  }

  restoreOriginalOrder() {
    if (!this.originalOrder) return
    this.originalOrder.forEach((nodes, container) => {
      nodes.forEach((node) => container.appendChild(node))
    })
  }

  compareNodes(a, b, key, direction) {
    if (key === "text") {
      const cmp = (a.node.dataset.sortText || "").localeCompare(b.node.dataset.sortText || "", "de")
      return cmp !== 0 ? cmp * direction : a.index - b.index
    }

    const attr = { quantity: "sortQuantity", unit_price: "sortUnitPrice", total: "sortTotal" }[key]
    const aValue = parseFloat(a.node.dataset[attr])
    const bValue = parseFloat(b.node.dataset[attr])
    const aMissing = Number.isNaN(aValue)
    const bMissing = Number.isNaN(bValue)
    if (aMissing && bMissing) return a.index - b.index
    if (aMissing) return 1
    if (bMissing) return -1
    if (aValue === bValue) return a.index - b.index
    return (aValue - bValue) * direction
  }

  // --- Drag & drop ---

  initSortables() {
    // One shared group name so a row can be dragged into another Titel, not just
    // reordered among its siblings; `put` also accepts drops from the catalog picker.
    // onEnd fires on the source container and onAdd on the destination, so a
    // cross-container move would reach the server twice — `onAdd` returns early for
    // tree-internal drags and lets the single onEnd carry the move.
    this.sortables = this.childrenTargets.map(
      (container) =>
        new Sortable(container, {
          draggable: "[data-id]",
          handle: "[data-drag-handle]",
          animation: 150,
          ghostClass: "bg-ghost",
          group: {
            name: "billing-offer-tree",
            pull: true,
            put: (to, from, dragEl) => from.el === to.el || this.canDrop(to, from, dragEl),
          },
          // Match the picker's pointer-based drag so a clone dragged out of the fixed
          // slide-over is recognised as a drop over these containers.
          forceFallback: true,
          fallbackOnBody: true,
          onEnd: (event) => this.reorder(event),
          onAdd: (event) => this.insertFromCatalog(event),
        }),
    )
  }

  // Refuse a drop of a Titel into its own subtree. The server-side cycle guard in
  // Billing::LineItemMover is the real defence; this only stops the DOM from showing a
  // move that is about to be rejected.
  canDrop(to, from, dragEl) {
    if (from.options.group.name === "boq-catalog") return true
    if (!dragEl) return true
    return !dragEl.contains(to.el)
  }

  // A catalog baustein was dragged from the picker into a line-item container. Insert
  // it at the drop location; the streamed tree re-render replaces the transient clone.
  // Rows dragged between two Titel of this tree also fire onAdd — those are handled by
  // the onEnd on the source container, which knows both ends of the move.
  insertFromCatalog(event) {
    if (event.from.classList.contains("billing-offer-children")) return

    insertCatalogEntry(event, {
      url: this.hasCatalogInsertUrlValue ? this.catalogInsertUrlValue : null,
      errorMessage: this.catalogInsertErrorValue,
    })
  }

  syncDragState() {
    const disabled = this.sortActive() || this.filterActive()
    ;(this.sortables || []).forEach((sortable) => sortable.option("disabled", disabled))
    this.element.classList.toggle("boq-drag-disabled", disabled)
    if (this.hasDragHintTarget) {
      this.dragHintTarget.classList.toggle("hidden", !(disabled && this.editableValue))
    }
  }

  // `moved_id` is sent only when the row changed Titel, which tells the server to
  // re-parent it (fixing the whole subtree's cached depth) before applying the new
  // sibling positions.
  reorder(event) {
    const container = event.to
    const parentId = container.dataset.parentId || null
    const positions = Array.from(container.children)
      .filter((el) => el.dataset.id)
      .map((el, index) => ({ id: parseInt(el.dataset.id), position: index + 1 }))
    if (positions.length === 0) return

    const crossedContainers = event.from !== event.to
    const movedId = crossedContainers ? parseInt(event.item.dataset.id) : null

    const token = document.head.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch(this.reorderUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token,
        Accept: "text/vnd.turbo-stream.html",
        "Content-type": "application/json",
      },
      body: JSON.stringify({ parent_id: parentId, positions: positions, moved_id: movedId }),
    })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html))
  }

  get storageKey() {
    return `billing-offer-tree-collapsed-${this.documentIdValue}`
  }

  get filterStorageKey() {
    return `billing-offer-tree-filter-${this.documentIdValue}`
  }
}
